import React, { useState, useEffect, useCallback, useRef } from 'react'
import styled from 'styled-components'
import {
  Input,
  Button,
  message,
  Modal,
  Tag,
  Upload,
  Divider,
  Drawer
} from 'antd'
import ImgCrop from 'antd-img-crop'
import {
  PlusOutlined,
  TagOutlined,
  HeartOutlined,
  MessageOutlined,
  WarningOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined
} from '@ant-design/icons'
import { PHOTO_URL } from '../utils/apiutils'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'
import { getPortfolioMediaList } from '../utils/portfolioMedia'
import { formatTz } from '../utils/timezone'

/**
 * antd Upload 的 picture-card 一律把 url 塞進 <img>，
 * 影片（含 blob: 影片）會直接變破圖。給影片項目一張 SVG placeholder 縮圖。
 */
const VIDEO_PLACEHOLDER_THUMB =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120">
      <rect width="120" height="120" fill="#262626"/>
      <circle cx="60" cy="52" r="24" fill="rgba(255,255,255,0.9)"/>
      <polygon points="53,40 53,64 74,52" fill="#262626"/>
      <text x="60" y="98" font-size="14" fill="#fff" text-anchor="middle" font-family="sans-serif">影片</text>
    </svg>`
  )

const PageContainer = styled.div`
  margin: 0 auto;
  padding: 20px;
  background-color: #fafafa;

  @media (max-width: 768px) {
    background: #fff;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;

  @media (max-width: 820px) {
    gap: 14px;
  }

  @media (max-width: 480px) {
    gap: 4px;
  }
`

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%;
  overflow: hidden;
  cursor: pointer;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`

const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const ThumbVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const PlayBadge = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 5;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  pointer-events: none;
`

const MediaCountBadge = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  z-index: 5;
  padding: 2px 8px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  pointer-events: none;
`

const ActionButtons = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 8px;
  z-index: 10;
  opacity: 1;
  transition: opacity 0.3s;

  @media (max-width: 820px) {
    opacity: 1;
  }

  ${ImageContainer}:hover & {
    opacity: 1;
  }
`

const ActionButton = styled(Button)`
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);

  /* 編輯按鈕（primary 類型，非 danger）*/
  &.ant-btn-primary:not(.ant-btn-dangerous) {
    background: rgba(24, 144, 255, 0.95);
    border-color: #1890ff;
    color: #fff;

    .anticon {
      color: #fff;
    }

    &:hover {
      background: #1890ff;
      border-color: #1890ff;
      color: #fff;

      .anticon {
        color: #fff;
      }
    }
  }

  /* 刪除按鈕（danger 類型）*/
  &.ant-btn-dangerous {
    background: rgba(255, 77, 79, 0.95);
    border-color: #ff4d4f;
    color: #fff;

    .anticon {
      color: #fff;
    }

    &:hover {
      background: #ff4d4f;
      border-color: #ff4d4f;
      color: #fff;

      .anticon {
        color: #fff;
      }
    }
  }

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 820px) {
    width: 32px;
    height: 32px;
  }
`

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 12px;
  opacity: 0;
  transition: opacity 0.3s;

  &:hover {
    opacity: 1;
  }
`

const Title = styled.h3`
  color: white;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`

const TagsPreview = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 8px;

  @media (max-width: 820px) {
    display: none;
  }
`

const MobileTagsPreview = styled.div`
  display: none;
  margin-top: 8px;

  @media (max-width: 820px) {
    display: block;
  }
`

const AddButton = styled(Button)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  background-color: #f0f2f5;
  border: 2px dashed #d9d9d9;
  border-radius: 50%;

  &:hover {
    border-color: #1890ff;
    color: #1890ff;
  }
  &.ant-btn.ant-btn-icon-only {
    width: 50px !important;
  }
`

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 16px;
`

const EnhancedTag = styled(Tag)`
  font-size: 16px;
  padding: 8px 16px;
  border-radius: 20px;
  margin-right: 12px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    transform: scale(1.05);
  }
  @media (max-width: 820px) {
    font-size: 14px;
    padding: 4px 8px;
    margin: 0px 4px 4px 0px;
  }
`

const ModalInfo = styled.div`
  width: 40%;
  padding: 0 100px;
  @media (max-width: 820px) {
    width: 100%;
    padding: 24px 0 0;
  }
`

const ModalTitle = styled.h2`
  margin-bottom: 16px;
  color: #262626;
`

const ModalDate = styled.p`
  color: #8e8e8e;
  margin-bottom: 16px;
`

const ModalTags = styled.div`
  margin-top: 16px;
  margin-bottom: 16px;
`

const AddPortfolioModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
  }

  .ant-modal-header {
    background-color: #f0f2f5;
    border-bottom: none;
    padding: 24px;
  }

  .ant-modal-body {
    padding: 24px;
  }

  .ant-modal-footer {
    border-top: none;
    padding: 24px;
  }
`

const UploadContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
`

const StyledSlider = styled(Slider)`
  margin-bottom: 20px;
  width: 100%;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  .slick-slide img {
    width: 100%;
    height: 500px;
    object-fit: contain;
    object-position: center;
    border-radius: 10px;
    transition: transform 0.3s;
  }

  .slick-prev:before,
  .slick-next:before {
    color: #d5728a;
  }

  @media (max-width: 768px) {
    max-width: 100%;

    .slick-slide img {
      height: 300px;
      padding: 0 10px;
    }
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const StyledModal = styled(Modal)`
  .ant-modal-content {
    max-width: 800px;
    width: 90%;
    margin: 0 auto;
  }

  .ant-modal-body {
    padding: 24px;
  }

  @media (max-width: 768px) {
    .ant-modal-content {
      width: 95%;
    }
  }
`

const OverlayTitle = styled(Title)`
  @media (max-width: 820px) {
    font-size: 12px;
    margin: 0;
  }
`

const PreviewModal = styled(Modal)`
  .ant-modal-content {
    width: 80%;
    max-width: 1000px;
    margin: 0 auto;
    border-radius: 24px;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(10px);
    overflow: hidden;
  }

  .ant-modal-body {
    padding: 0;
    display: flex;
    flex-direction: row;
    min-height: 500px;
    max-height: 80vh;

    @media (max-width: 768px) {
      flex-direction: column;
      min-height: auto;
    }
  }

  .slider-container {
    flex: 1.5;
    position: relative;
    background: #fafafa;

    /* slick 預設把左右箭頭放在 -25px、圓點放在 -25px，
       但 .ant-modal-content 是 overflow:hidden，會把它們整個裁掉，
       使用者因此無法切到第二張。這裡把它們拉回容器內部。 */
    .slick-prev {
      left: 12px;
      z-index: 2;
    }

    .slick-next {
      right: 12px;
      z-index: 2;
    }

    .slick-prev,
    .slick-next {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.45);
    }

    .slick-prev:hover,
    .slick-next:hover {
      background: rgba(0, 0, 0, 0.65);
    }

    .slick-prev:before,
    .slick-next:before {
      color: #fff;
      opacity: 1;
      font-size: 18px;
    }

    .slick-dots {
      bottom: 10px;
    }

    .slick-dots li button:before {
      color: #fff;
      opacity: 0.6;
      text-shadow: 0 0 3px rgba(0, 0, 0, 0.8);
    }

    .slick-dots li.slick-active button:before {
      color: #fff;
      opacity: 1;
    }

    .slick-slider {
      .slick-list,
      .slick-track {
        height: 100%;
      }
    }

    img,
    video {
      height: 500px;
      object-fit: contain;
      object-position: center;
      width: 100%;
      background: #000;

      @media (max-width: 820px) {
        height: 200px;
      }
    }
  }

  .info-container {
    flex: 1;
    padding: 32px;
    background: #fff;
    overflow-y: auto;
  }

  .ant-modal-close {
    top: 16px;
    right: 16px;
    color: ${(props) => (props.theme.dark ? '#fff' : '#000')};
  }
  @media (max-width: 820px) {
    .ant-modal-content {
      width: 100%;
      margin: 0 auto;
      border-radius: 24px;
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(10px);
      overflow: hidden;
    }
    .info-container {
      padding: 0px 10px;
    }
  }
`

const AddPortfolioDrawer = styled(Drawer)`
  .ant-drawer-content-wrapper {
    width: 500px !important;

    @media (max-width: 768px) {
      width: 100% !important;
    }
  }

  .ant-drawer-header {
    padding: 24px 32px;
    border-bottom: 1px solid #f0f0f0;
  }

  .ant-drawer-body {
    padding: 24px 32px;
  }

  .ant-drawer-footer {
    border-top: 1px solid #f0f0f0;
    padding: 16px 32px;

    .ant-btn {
      height: 44px;
      padding: 0 32px;
      border-radius: 22px;
      font-size: 16px;

      &.ant-btn-primary {
        background: linear-gradient(45deg, #1890ff, #36cfc9);
        border: none;
        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
      }
    }
  }

  .upload-area {
    background: #fafafa;
    border-radius: 16px;
    padding: 24px;
    margin: 24px 0;

    .ant-upload-list {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 16px;
      margin-top: 16px;
    }

    .ant-upload.ant-upload-select {
      width: 100%;
      height: 150px;
      border-radius: 12px;
      border: 2px dashed #d9d9d9;

      &:hover {
        border-color: #1890ff;
      }
    }
  }
`

const LimitWarning = styled.div`
  background: linear-gradient(135deg, #fffbe6 0%, #fff7e6 100%);
  border: 1px solid #ffe58f;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;

  .warning-icon {
    color: #faad14;
    font-size: 20px;
  }

  .warning-content {
    flex: 1;

    h4 {
      color: #d46b08;
      margin: 0 0 4px 0;
      font-size: 16px;
    }

    p {
      color: #8c8c8c;
      margin: 0;
      font-size: 14px;
    }
  }

  @media (max-width: 768px) {
    padding: 12px;

    .warning-content {
      h4 {
        font-size: 14px;
      }

      p {
        font-size: 12px;
      }
    }
  }
`

const ModalContent = ({ selectedPortfolio }) => {
  if (
    !selectedPortfolio ||
    !selectedPortfolio.imageUrls ||
    selectedPortfolio.imageUrls.length === 0
  ) {
    return <div>沒有可顯示的圖片</div>
  }

  const mediaList = getPortfolioMediaList(selectedPortfolio)

  const settings = {
    dots: mediaList.length > 1,
    infinite: mediaList.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: mediaList.length > 1,
    swipe: true,
    // 影片要能拖曳進度條，slick 的 draggable 會把 mousedown 吃掉
    draggable: false
  }

  return (
    <>
      <div className='slider-container'>
        <StyledSlider {...settings}>
          {mediaList.map((item, index) => (
            <div key={`${item.url}-${index}`}>
              {item.type === 'video' ? (
                <video
                  key={item.url}
                  controls
                  playsInline
                  preload='metadata'
                  // #t=0.1 讓瀏覽器畫出第一個影格，否則只會看到黑色方塊
                  src={`${PHOTO_URL}${item.url}#t=0.1`}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                />
              ) : (
                <img
                  src={`${PHOTO_URL}${item.url}`}
                  alt={`${selectedPortfolio.title} - ${index + 1}`}
                />
              )}
            </div>
          ))}
        </StyledSlider>
        {mediaList.length > 1 && (
          <div
            style={{
              position: 'absolute',
              top: 12,
              left: 12,
              padding: '2px 10px',
              borderRadius: 12,
              background: 'rgba(0,0,0,0.55)',
              color: '#fff',
              fontSize: 12,
              zIndex: 2,
              pointerEvents: 'none'
            }}
          >
            共 {mediaList.length} 個
          </div>
        )}
      </div>
      <div className='info-container'>
        <ModalTitle>{selectedPortfolio.title}</ModalTitle>
        <ModalDate>
          {formatTz(selectedPortfolio.createdAt, 'YYYY年M月D日')}
        </ModalDate>
        <div className='content'>{selectedPortfolio.content}</div>
        <ModalTags>
          {selectedPortfolio.tags &&
            selectedPortfolio.tags.map((tag, index) => (
              <EnhancedTag key={index} color='#1890ff'>
                {tag}
              </EnhancedTag>
            ))}
        </ModalTags>
      </div>
    </>
  )
}

const EmployeePortfolio = ({ employeeId }) => {
  const [portfolios, setPortfolios] = useState([])
  const [tags, setTags] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedPortfolio, setSelectedPortfolio] = useState(null)
  const [isAddModalVisible, setIsAddModalVisible] = useState(false)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [editingPortfolio, setEditingPortfolio] = useState(null)
  const [newPortfolio, setNewPortfolio] = useState({
    title: '',
    content: '',
    images: [],
    tags: []
  })
  const [isLoading, setIsLoading] = useState(false)
  const [unsavedTags, setUnsavedTags] = useState([])
  const [fileList, setFileList] = useState([])
  const [editFileList, setEditFileList] = useState([])
  const [deletedPhotos, setDeletedPhotos] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const { user } = useAuth()
  const businessId = user.businessId
  const plan = user?.plan
  const [shouldShowCrop, setShouldShowCrop] = useState(true)
  const [hasHeicInFileList, setHasHeicInFileList] = useState(false)
  const [hasHeicInEditFileList, setHasHeicInEditFileList] = useState(false)
  const [totalPhotos, setTotalPhotos] = useState(0)
  const FREE_TIER_LIMIT = 20
  const MAX_VIDEO_SIZE_MB = 40
  const MAX_VIDEO_DURATION_SECONDS = 90

  // 使用 ref 來同步檢測 HEIC，避免狀態更新延遲
  const heicFileRef = useRef(new Set())
  const heicEditFileRef = useRef(new Set())

  const getVideoDuration = (fileObj) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(fileObj)
      const video = document.createElement('video')
      video.preload = 'metadata'
      video.onloadedmetadata = () => {
        const duration = video.duration || 0
        URL.revokeObjectURL(url)
        resolve(duration)
      }
      video.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('無法讀取影片長度'))
      }
      video.src = url
    })

  const fetchPortfolios = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await api.get(
        `/api/employees/${businessId}/employees/${employeeId}/portfolio-and-tags`
      )
      const formattedPortfolios = response.data.portfolio.map((portfolio) => ({
        ...portfolio,
        images: portfolio.imageUrls || [],
        mediaTypes: portfolio.mediaTypes,
        id: portfolio._id
      }))
      setPortfolios(formattedPortfolios)
      setTags(response.data.portfolioTags)

      const total = formattedPortfolios.reduce(
        (sum, portfolio) => sum + (portfolio.imageUrls?.length || 0),
        0
      )
      setTotalPhotos(total)
    } catch (error) {
      message.error('獲取作品集失敗')
    }
    setIsLoading(false)
  }, [businessId, employeeId])

  useEffect(() => {
    fetchPortfolios()
  }, [fetchPortfolios])

  const handleInputChange = (e) => {
    setInputValue(e.target.value)
  }

  const handleTagAdd = () => {
    if (
      inputValue &&
      tags.indexOf(inputValue) === -1 &&
      unsavedTags.indexOf(inputValue) === -1
    ) {
      setUnsavedTags([...unsavedTags, inputValue])
      setInputValue('')
    }
  }

  const handleUnsavedTagClose = (removedTag) => {
    const newUnsavedTags = unsavedTags.filter((tag) => tag !== removedTag)
    setUnsavedTags(newUnsavedTags)
  }

  const handleSaveTags = async () => {
    if (unsavedTags.length === 0) {
      return
    }
    setIsLoading(true)
    try {
      await api.post(
        `/api/employees/${businessId}/${employeeId}/portfolio-tags`,
        { tags: unsavedTags }
      )
      setTags([...tags, ...unsavedTags])
      setUnsavedTags([])
      message.success('標籤保存成功')
    } catch (error) {
      message.error('保存標籤失敗')
    }
    setIsLoading(false)
  }

  const handleClose = (removedTag) => {
    const newTags = tags.filter((tag) => tag !== removedTag)
    setTags(newTags)
  }

  const showModal = (portfolio) => {
    setSelectedPortfolio(portfolio)
    setIsModalVisible(true)
  }

  const showAddModal = () => {
    if (tags.length === 0) {
      message.warning('請先添加至少一個標籤')
      return
    }
    // 重置狀態
    setShouldShowCrop(true)
    setHasHeicInFileList(false)
    heicFileRef.current.clear()
    setIsAddModalVisible(true)
  }

  const customRequest = ({ file, onSuccess }) => {
    onSuccess('ok')
  }

  // 在文件選擇時立即檢測 HEIC，阻止 ImgCrop 處理
  const beforeUpload = (file, fileList) => {
    const fileName = (file.name || '').toLowerCase()
    const isHeic = /\.(heic|heif)$/i.test(fileName)

    // 檢查當前文件列表中是否已有 HEIC
    const currentFileList = fileList || []
    const hasHeicInCurrentList = currentFileList.some((f) => {
      const fName = (f.name || f.originFileObj?.name || '').toLowerCase()
      return /\.(heic|heif)$/i.test(fName) || f.isHeic
    })

    // 如果是 HEIC 文件，立即更新 ref 和狀態，阻止 ImgCrop 顯示裁剪 modal
    if (isHeic) {
      heicFileRef.current.add(file.uid || file.name)
    }
    if (isHeic || hasHeicInCurrentList) {
      setHasHeicInFileList(true)
      setShouldShowCrop(false)
    }

    return true // 允許文件繼續處理
  }

  // ImgCrop 的 beforeCrop 回調，在裁剪前攔截 HEIC 文件
  const beforeCrop = (file) => {
    const mime = file.type || file.originFileObj?.type || ''
    if (mime.startsWith('video/')) return false
    const fileName = (file.name || file.originFileObj?.name || '').toLowerCase()
    const isHeic =
      /\.(heic|heif)$/i.test(fileName) ||
      heicFileRef.current.has(file.uid || file.name)

    // 如果是 HEIC 文件，返回 false 阻止裁剪 modal 顯示
    if (isHeic) {
      return false
    }
    return true
  }

  // 編輯模式專用的 beforeUpload
  const beforeUploadEdit = (file, fileList) => {
    const fileName = (file.name || '').toLowerCase()
    const isHeic = /\.(heic|heif)$/i.test(fileName)

    // 檢查當前文件列表中是否已有 HEIC
    const currentFileList = fileList || []
    const hasHeicInCurrentList = currentFileList.some((f) => {
      if (f.isExisting) return false
      const fName = (f.name || f.originFileObj?.name || '').toLowerCase()
      return /\.(heic|heif)$/i.test(fName) || f.isHeic
    })

    // 如果是 HEIC 文件，立即更新 ref 和狀態，阻止 ImgCrop 顯示裁剪 modal
    if (isHeic) {
      heicEditFileRef.current.add(file.uid || file.name)
    }
    if (isHeic || hasHeicInCurrentList) {
      setHasHeicInEditFileList(true)
      setShouldShowCrop(false)
    }

    return true // 允許文件繼續處理
  }

  // 編輯模式專用的 beforeCrop
  const beforeCropEdit = (file) => {
    const mime = file.type || file.originFileObj?.type || ''
    if (mime.startsWith('video/')) return false
    const fileName = (file.name || file.originFileObj?.name || '').toLowerCase()
    const isHeic =
      /\.(heic|heif)$/i.test(fileName) ||
      heicEditFileRef.current.has(file.uid || file.name)

    // 如果是 HEIC 文件，返回 false 阻止裁剪 modal 顯示
    if (isHeic) {
      return false
    }
    return true
  }

  // 創建 HEIC 文件的替代預覽圖（灰底+打勾）
  const createHeicPlaceholder = () => {
    const canvas = document.createElement('canvas')
    canvas.width = 200
    canvas.height = 200
    const ctx = canvas.getContext('2d')

    // 繪製灰色背景
    ctx.fillStyle = '#f0f0f0'
    ctx.fillRect(0, 0, 200, 200)

    // 繪製打勾圖標（簡單的 SVG 路徑）
    ctx.strokeStyle = '#52c41a'
    ctx.lineWidth = 8
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.beginPath()
    ctx.moveTo(60, 100)
    ctx.lineTo(90, 130)
    ctx.lineTo(140, 70)
    ctx.stroke()

    // 添加文字
    ctx.fillStyle = '#8c8c8c'
    ctx.font = '14px Arial'
    ctx.textAlign = 'center'
    ctx.fillText('格式不支持預覽，已可上傳', 100, 170)

    return canvas.toDataURL('image/png')
  }

  const handleImageUpload = async ({ file, fileList: newFileList }) => {
    if (plan === 'free') {
      const totalNewPhotos = totalPhotos + newFileList.length
      if (totalNewPhotos > FREE_TIER_LIMIT) {
        message.error(`免費用戶最多只能上傳 ${FREE_TIER_LIMIT} 張照片`)
        const allowedNewPhotos = FREE_TIER_LIMIT - totalPhotos
        const limitedFileList = newFileList.slice(0, allowedNewPhotos)
        setFileList(limitedFileList)
        return
      }
    }

    // 檢查文件列表中是否有 HEIC 文件
    const fileTypes = newFileList.map((f) => {
      const fileName = (f.name || f.originFileObj?.name || '').toLowerCase()
      if (/\.(heic|heif)$/i.test(fileName)) return 'HEIC'
      if (/\.(jpeg|jpg)$/i.test(fileName)) return 'JPEG'
      if (/\.(png)$/i.test(fileName)) return 'PNG'
      if (/\.(gif)$/i.test(fileName)) return 'GIF'
      return 'UNKNOWN'
    })

    const hasHeicInList = newFileList.some((f) => {
      const fileName = (f.name || f.originFileObj?.name || '').toLowerCase()
      return /\.(heic|heif)$/i.test(fileName)
    })

    setHasHeicInFileList(hasHeicInList)

    // 處理文件：HEIC 使用替代預覽圖，其他格式正常處理
    const placeholderUrl = createHeicPlaceholder()
    const processedFiles = await Promise.all(
      newFileList.map(async (f) => {
        if (f.originFileObj && !f.url && !f.preview) {
          const vidMime = f.originFileObj?.type || ''
          if (vidMime.startsWith('video/')) {
            const maxBytes = MAX_VIDEO_SIZE_MB * 1024 * 1024
            if (f.originFileObj.size > maxBytes) {
              message.error(
                `影片「${f.name}」超過 ${MAX_VIDEO_SIZE_MB}MB，請壓縮後再上傳`
              )
              return null
            }
            try {
              const duration = await getVideoDuration(f.originFileObj)
              if (duration > MAX_VIDEO_DURATION_SECONDS) {
                message.error(
                  `影片「${f.name}」超過 ${MAX_VIDEO_DURATION_SECONDS} 秒，請剪輯後再上傳`
                )
                return null
              }
            } catch {
              message.error(`無法讀取影片「${f.name}」時長，請更換檔案重試`)
              return null
            }
            const vidUrl = URL.createObjectURL(f.originFileObj)
            return {
              ...f,
              status: 'done',
              url: vidUrl,
              preview: vidUrl,
              // picture-card 會把 url 塞進 <img>，blob 影片會變破圖，
              // 這裡給一個影片placeholder 縮圖
              thumbUrl: VIDEO_PLACEHOLDER_THUMB,
              isVideo: true
            }
          }
          const fileName = (f.name || f.originFileObj?.name || '').toLowerCase()
          const isHeic = /\.(heic|heif)$/i.test(fileName)

          if (isHeic) {
            // HEIC 文件使用替代預覽圖，不裁剪
            return {
              ...f,
              status: 'done',
              url: placeholderUrl,
              preview: placeholderUrl,
              isHeic: true
            }
          } else {
            // 非 HEIC 文件正常處理
            const previewUrl = await new Promise((resolve) => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result)
              reader.onerror = () => resolve(null)
              reader.readAsDataURL(f.originFileObj)
            })
            return {
              ...f,
              status: 'done',
              url: previewUrl,
              preview: previewUrl
            }
          }
        }
        return {
          ...f,
          status: 'done'
        }
      })
    )

    const filteredFiles = processedFiles.filter(Boolean)
    const hasVideoInList = filteredFiles.some(
      (f) => f.isVideo || (f.originFileObj?.type || '').startsWith('video/')
    )
    const newShouldShowCrop = !hasHeicInList && !hasVideoInList
    setShouldShowCrop(newShouldShowCrop)
    setFileList(filteredFiles)
  }

  const onPreview = async (file) => {
    if (file.isVideo && file.originFileObj) {
      const u = URL.createObjectURL(file.originFileObj)
      window.open(u, '_blank')
      return
    }
    // 如果是 HEIC 文件，不允許預覽
    if (file.isHeic) {
      message.info('HEIC 格式文件將由後端處理，暫不提供預覽')
      return
    }

    let previewUrl = file.url

    // 如果沒有 url，則從文件對象創建
    if (!previewUrl) {
      previewUrl = await new Promise((resolve) => {
        const reader = new FileReader()
        reader.readAsDataURL(file.originFileObj)
        reader.onload = () => resolve(reader.result)
      })
    }

    // 創建新窗口並顯示圖片
    const newWindow = window.open('', '_blank')
    newWindow.document.write(`
      <html>
        <head>
          <title>圖片預覽</title>
          <style>
            body {
              margin: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              background: #000;
            }
            img {
              max-width: 100%;
              max-height: 100vh;
              object-fit: contain;
            }
          </style>
        </head>
        <body>
          <img src="${previewUrl}" alt="Preview" />
        </body>
      </html>
    `)
    newWindow.document.close()
  }

  const handleAddPortfolio = async () => {
    if (fileList.length === 0) {
      message.warning('請至少上傳一張圖片或一支影片')
      return
    }
    if (!newPortfolio.title || !newPortfolio.content) {
      message.warning('請填寫標題和內容')
      return
    }
    if (newPortfolio.tags.length === 0) {
      message.warning('請至少選擇一個標籤')
      return
    }

    const formData = new FormData()
    formData.append('title', newPortfolio.title)
    formData.append('content', newPortfolio.content)
    formData.append('tags', JSON.stringify(newPortfolio.tags))

    fileList.forEach((file) => {
      if (file.originFileObj) {
        formData.append('photos', file.originFileObj)
      }
    })

    setIsLoading(true)
    setIsUploading(true)
    try {
      const response = await api.post(
        `/api/employees/${businessId}/${employeeId}/portfolio`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
      const work = response.data.work || response.data
      const newPortfolioItem = {
        ...work,
        imageUrls: work.imageUrls || [],
        mediaTypes: work.mediaTypes,
        id: work._id
      }
      setPortfolios([...portfolios, newPortfolioItem])
      setIsAddModalVisible(false)
      setNewPortfolio({ title: '', content: '', images: [], tags: [] })
      setFileList([])
      message.success('新增作品集成功！')
      await fetchPortfolios() // 重新獲取所有作品集
    } catch (error) {
      // 把後端的實際原因顯示出來（例如方案作品數量上限、檔案格式錯誤）
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          '新增作品集失敗'
      )
    }
    setIsLoading(false)
    setIsUploading(false)
  }

  const handleDeletePortfolio = async (workId, e) => {
    e.stopPropagation() // 阻止觸發點擊事件
    Modal.confirm({
      title: '確定要刪除這個作品嗎？',
      content: '刪除後無法恢復',
      okText: '確定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.delete(
            `/api/employees/${businessId}/${employeeId}/portfolio/${workId}`
          )
          setPortfolios(
            portfolios.filter((portfolio) => portfolio.id !== workId)
          )
          message.success('刪除作品成功')
          await fetchPortfolios() // 重新獲取以更新照片總數
        } catch (error) {
          message.error('刪除作品失敗')
        }
      }
    })
  }

  const showEditModal = (portfolio, e) => {
    e.stopPropagation() // 阻止觸發點擊事件
    setEditingPortfolio(portfolio)

    // 重置狀態
    setShouldShowCrop(true)
    setHasHeicInEditFileList(false)
    heicEditFileRef.current.clear()

    // 初始化編輯表單
    setNewPortfolio({
      title: portfolio.title || '',
      content: portfolio.content || '',
      images: portfolio.imageUrls || [],
      tags: portfolio.tags || []
    })

    // 初始化文件列表（現有圖片／影片）
    // 必須以 mediaTypes 判斷，不能只看副檔名 —— B2 上的舊檔案 key 沒有副檔名，
    // 只看副檔名會把影片當成圖片，導致縮圖破圖、而且會誤開啟裁切工具。
    const existingFiles = getPortfolioMediaList(portfolio).map(
      (media, index) => ({
        uid: `existing-${index}`,
        name: media.url.split('/').pop(),
        status: 'done',
        url: `${PHOTO_URL}${media.url}`,
        thumbUrl:
          media.type === 'video' ? VIDEO_PLACEHOLDER_THUMB : undefined,
        isExisting: true,
        photoUrl: media.url,
        isVideo: media.type === 'video'
      })
    )
    setEditFileList(existingFiles)
    setDeletedPhotos([])
    setShouldShowCrop(true) // 重置為允許裁剪
    setHasHeicInEditFileList(false) // 重置 HEIC 標記
    setIsEditModalVisible(true)
  }

  const handleEditImageUpload = async ({ file, fileList: newFileList }) => {
    if (plan === 'free') {
      const existingCount = editFileList.filter((f) => f.isExisting).length
      const newCount = newFileList.filter((f) => !f.isExisting).length
      const totalNewPhotos = existingCount - deletedPhotos.length + newCount
      if (totalNewPhotos > FREE_TIER_LIMIT) {
        message.error(`免費用戶最多只能上傳 ${FREE_TIER_LIMIT} 張照片`)
        return
      }
    }

    // 檢查文件列表中是否有 HEIC 文件
    const fileTypes = newFileList.map((f) => {
      if (f.isExisting) return 'EXISTING'
      const fileName = (f.name || f.originFileObj?.name || '').toLowerCase()
      if (/\.(heic|heif)$/i.test(fileName)) return 'HEIC'
      if (/\.(jpeg|jpg)$/i.test(fileName)) return 'JPEG'
      if (/\.(png)$/i.test(fileName)) return 'PNG'
      if (/\.(gif)$/i.test(fileName)) return 'GIF'
      return 'UNKNOWN'
    })

    const hasHeicInList = newFileList.some((f) => {
      const fileName = (f.name || f.originFileObj?.name || '').toLowerCase()
      return /\.(heic|heif)$/i.test(fileName)
    })

    setHasHeicInEditFileList(hasHeicInList)

    // 處理文件：HEIC 使用替代預覽圖，其他格式正常處理
    const placeholderUrl = createHeicPlaceholder()
    const processedFiles = await Promise.all(
      newFileList.map(async (f) => {
        // 如果是已存在的文件，直接返回
        if (f.isExisting) {
          return f
        }

        if (f.originFileObj && !f.url && !f.preview) {
          const vidMime = f.originFileObj?.type || ''
          if (vidMime.startsWith('video/')) {
            const maxBytes = MAX_VIDEO_SIZE_MB * 1024 * 1024
            if (f.originFileObj.size > maxBytes) {
              message.error(
                `影片「${f.name}」超過 ${MAX_VIDEO_SIZE_MB}MB，請壓縮後再上傳`
              )
              return null
            }
            try {
              const duration = await getVideoDuration(f.originFileObj)
              if (duration > MAX_VIDEO_DURATION_SECONDS) {
                message.error(
                  `影片「${f.name}」超過 ${MAX_VIDEO_DURATION_SECONDS} 秒，請剪輯後再上傳`
                )
                return null
              }
            } catch {
              message.error(`無法讀取影片「${f.name}」時長，請更換檔案重試`)
              return null
            }
            const vidUrl = URL.createObjectURL(f.originFileObj)
            return {
              ...f,
              status: 'done',
              url: vidUrl,
              preview: vidUrl,
              // picture-card 會把 url 塞進 <img>，blob 影片會變破圖，
              // 這裡給一個影片placeholder 縮圖
              thumbUrl: VIDEO_PLACEHOLDER_THUMB,
              isVideo: true
            }
          }
          const fileName = (f.name || f.originFileObj?.name || '').toLowerCase()
          const isHeic = /\.(heic|heif)$/i.test(fileName)

          if (isHeic) {
            // HEIC 文件使用替代預覽圖，不裁剪
            return {
              ...f,
              status: 'done',
              url: placeholderUrl,
              preview: placeholderUrl,
              isHeic: true
            }
          } else {
            // 非 HEIC 文件正常處理
            const previewUrl = await new Promise((resolve) => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result)
              reader.onerror = () => resolve(null)
              reader.readAsDataURL(f.originFileObj)
            })
            return {
              ...f,
              status: 'done',
              url: previewUrl,
              preview: previewUrl
            }
          }
        }
        return {
          ...f,
          status: 'done'
        }
      })
    )

    const filteredFiles = processedFiles.filter(Boolean)
    const hasVideoInList = filteredFiles.some((f) => {
      if (f.isExisting) {
        return (
          f.isVideo ||
          /\.(mp4|webm|mov|m4v)(\?|$)/i.test(String(f.photoUrl || ''))
        )
      }
      return (f.originFileObj?.type || '').startsWith('video/') || f.isVideo
    })
    const newShouldShowCrop = !hasHeicInList && !hasVideoInList
    setShouldShowCrop(newShouldShowCrop)
    setEditFileList(filteredFiles)
  }

  const handleEditImageRemove = (file) => {
    if (file.isExisting && file.photoUrl) {
      // 標記為刪除
      setDeletedPhotos((prev) => [...prev, file.photoUrl])
    }
  }

  const handleUpdatePortfolio = async () => {
    if (editFileList.length === 0) {
      message.warning('請至少保留一張圖片或一支影片')
      return
    }
    if (!newPortfolio.title || !newPortfolio.content) {
      message.warning('請填寫標題和內容')
      return
    }
    if (newPortfolio.tags.length === 0) {
      message.warning('請至少選擇一個標籤')
      return
    }

    const formData = new FormData()
    formData.append('title', newPortfolio.title)
    formData.append('content', newPortfolio.content)
    formData.append('tags', JSON.stringify(newPortfolio.tags))

    // 添加要刪除的照片
    // 必須送 JSON 字串：只刪一個時 multer 會給後端「字串」而非陣列，
    // 後端舊版直接 JSON.parse 會丟例外，導致「只刪一張」永遠 400 失敗。
    if (deletedPhotos.length > 0) {
      formData.append('deletedPhotos', JSON.stringify(deletedPhotos))
    }

    // 添加新上傳的照片
    editFileList.forEach((file) => {
      if (file.originFileObj && !file.isExisting) {
        formData.append('photos', file.originFileObj)
      }
    })

    setIsLoading(true)
    setIsUploading(true)
    try {
      await api.put(
        `/api/employees/${businessId}/${employeeId}/portfolio/${editingPortfolio.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
      setIsEditModalVisible(false)
      setEditingPortfolio(null)
      setNewPortfolio({ title: '', content: '', images: [], tags: [] })
      setEditFileList([])
      setDeletedPhotos([])
      setHasHeicInEditFileList(false) // 重置 HEIC 標記
      setShouldShowCrop(true) // 重置為允許裁剪
      message.success('更新作品集成功！')
      await fetchPortfolios() // 重新獲取所有作品集
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          '更新作品集失敗'
      )
    }
    setIsLoading(false)
    setIsUploading(false)
  }

  return (
    <PageContainer>
      {plan === 'free' && (
        <LimitWarning>
          <WarningOutlined className='warning-icon' />
          <div className='warning-content'>
            <h4>免費用戶限制</h4>
            <p>
              您當前已使用 {totalPhotos}/{FREE_TIER_LIMIT}{' '}
              張照片配額。升級至專業版可解除限制！
            </p>
          </div>
          {totalPhotos >= FREE_TIER_LIMIT && (
            <Button type='primary' danger>
              升級方案
            </Button>
          )}
        </LimitWarning>
      )}
      <h3>標籤清單</h3>
      <TagContainer>
        {tags.map((tag) => (
          <EnhancedTag key={tag} closable onClose={() => handleClose(tag)}>
            {tag}
          </EnhancedTag>
        ))}
        {unsavedTags.map((tag) => (
          <EnhancedTag
            key={tag}
            color='blue'
            closable
            onClose={() => handleUnsavedTagClose(tag)}
          >
            {tag} (未保存)
          </EnhancedTag>
        ))}
      </TagContainer>
      <Input
        placeholder='添加新標籤（按Enter確認）'
        prefix={<TagOutlined />}
        value={inputValue}
        onChange={handleInputChange}
        onPressEnter={handleTagAdd}
        style={{ marginBottom: '16px' }}
      />
      <Button
        type='primary'
        onClick={handleSaveTags}
        disabled={unsavedTags.length === 0}
        loading={isLoading}
        style={{ marginBottom: '16px' }}
      >
        保存新標籤
      </Button>
      <p style={{ marginBottom: '16px', color: '#8c8c8c' }}>
        提示：請先新增標籤再新增作品
      </p>
      <Divider />
      <h3>作品集</h3>
      <Grid>
        {portfolios.map((portfolio) => {
          const mediaList = getPortfolioMediaList(portfolio)
          const first = mediaList[0]
          return (
          <ImageContainer
            key={portfolio.id}
            onClick={() => showModal(portfolio)}
          >
            {first?.type === 'video' ? (
              <>
                <ThumbVideo
                  src={`${PHOTO_URL}${first.url}#t=0.1`}
                  muted
                  playsInline
                  preload='metadata'
                />
                <PlayBadge>▶</PlayBadge>
              </>
            ) : (
            <Image
              src={first?.url ? `${PHOTO_URL}${first.url}` : '/placeholder.png'}
              alt={portfolio.title}
            />
            )}
            {mediaList.length > 1 && (
              <MediaCountBadge>1/{mediaList.length}</MediaCountBadge>
            )}

            <ActionButtons>
              <ActionButton
                type='primary'
                icon={<EditOutlined />}
                onClick={(e) => showEditModal(portfolio, e)}
                size='small'
              />
              <ActionButton
                type='primary'
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => handleDeletePortfolio(portfolio.id, e)}
                size='small'
              />
            </ActionButtons>

            <Overlay>
              <OverlayTitle>{portfolio.title}</OverlayTitle>
              <TagsPreview>
                {(portfolio.tags || []).slice(0, 2).map((tag, index) => (
                  <EnhancedTag key={index} color='#1890ff'>
                    {tag}
                  </EnhancedTag>
                ))}
                {(portfolio.tags || []).length > 2 && (
                  <EnhancedTag color='#1890ff'>...</EnhancedTag>
                )}
              </TagsPreview>
              <MobileTagsPreview>
                <EnhancedTag color='#1890ff'>...</EnhancedTag>
              </MobileTagsPreview>
            </Overlay>
          </ImageContainer>
          )
        })}
        <ImageContainer>
          <AddButton icon={<PlusOutlined />} onClick={showAddModal} />
        </ImageContainer>
      </Grid>
      <PreviewModal
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false)
          setSelectedPortfolio(null)
        }}
        footer={null}
        width='90%'
        centered
      >
        {selectedPortfolio && (
          <ModalContent selectedPortfolio={selectedPortfolio} />
        )}
      </PreviewModal>
      <AddPortfolioDrawer
        title='新增作品集'
        placement='right'
        open={isAddModalVisible}
        onClose={() => setIsAddModalVisible(false)}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button
              onClick={() => setIsAddModalVisible(false)}
              style={{ marginRight: 8 }}
            >
              取消
            </Button>
            <Button
              type='primary'
              onClick={handleAddPortfolio}
              loading={isUploading}
              disabled={isUploading}
            >
              發布
            </Button>
          </div>
        }
      >
        <Input
          size='large'
          placeholder='作品標題'
          value={newPortfolio.title}
          onChange={(e) =>
            setNewPortfolio({ ...newPortfolio, title: e.target.value })
          }
        />

        <div className='upload-area'>
          {(() => {
            const fileTypes = fileList.map((f) => {
              const fileName = (
                f.name ||
                f.originFileObj?.name ||
                ''
              ).toLowerCase()
              if (/\.(heic|heif)$/i.test(fileName) || f.isHeic) return 'HEIC'
              if (/\.(jpeg|jpg)$/i.test(fileName)) return 'JPEG'
              if (/\.(png)$/i.test(fileName)) return 'PNG'
              if (/\.(gif)$/i.test(fileName)) return 'GIF'
              return 'OTHER'
            })
            const hasVideo = fileList.some(
              (f) =>
                f.isVideo || (f.originFileObj?.type || '').startsWith('video/')
            )
            return shouldShowCrop && !hasVideo ? (
              <ImgCrop
                rotate
                aspect={1}
                quality={1}
                modalTitle='裁剪圖片'
                modalOk='確定'
                modalCancel='取消'
                beforeCrop={beforeCrop}
              >
                <Upload
                  listType='picture-card'
                  fileList={fileList}
                  onChange={handleImageUpload}
                  onPreview={onPreview}
                  beforeUpload={beforeUpload}
                  multiple
                  disabled={isUploading}
                  customRequest={customRequest}
                  accept='image/*,video/mp4,video/quicktime,video/webm'
                >
                  {fileList.length < 5 && (
                    <div>
                      <PlusOutlined
                        style={{ fontSize: '24px', marginBottom: '8px' }}
                      />
                      <div>上傳照片／影片</div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#888',
                          marginTop: '8px'
                        }}
                      >
                        圖片或 MP4／MOV／WEBM，最長 90 秒、單檔最大 40MB
                      </div>
                    </div>
                  )}
                </Upload>
              </ImgCrop>
            ) : (
              <Upload
                listType='picture-card'
                fileList={fileList}
                onChange={handleImageUpload}
                onPreview={onPreview}
                beforeUpload={beforeUpload}
                multiple
                disabled={isUploading}
                customRequest={customRequest}
                accept='image/*,video/mp4,video/quicktime,video/webm'
              >
                {fileList.length < 5 && (
                  <div>
                    <PlusOutlined
                      style={{ fontSize: '24px', marginBottom: '8px' }}
                    />
                    <div>上傳照片／影片</div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#888',
                        marginTop: '8px'
                      }}
                    >
                      圖片或 MP4／MOV／WEBM，最長 90 秒、單檔最大 40MB
                    </div>
                  </div>
                )}
              </Upload>
            )
          })()}
        </div>

        <Input.TextArea
          placeholder='作品內容'
          value={newPortfolio.content}
          onChange={(e) =>
            setNewPortfolio({ ...newPortfolio, content: e.target.value })
          }
          rows={6}
        />

        <Divider orientation='left'>選擇標籤</Divider>
        <TagContainer>
          {tags.map((tag) => (
            <EnhancedTag
              key={tag}
              color={newPortfolio.tags.includes(tag) ? '#1890ff' : ''}
              onClick={() => {
                const newTags = newPortfolio.tags.includes(tag)
                  ? newPortfolio.tags.filter((t) => t !== tag)
                  : [...newPortfolio.tags, tag]
                setNewPortfolio({ ...newPortfolio, tags: newTags })
              }}
            >
              {tag}
            </EnhancedTag>
          ))}
        </TagContainer>
      </AddPortfolioDrawer>
      <AddPortfolioDrawer
        title='編輯作品集'
        placement='right'
        open={isEditModalVisible}
        onClose={() => {
          setIsEditModalVisible(false)
          setEditingPortfolio(null)
          setEditFileList([])
          setDeletedPhotos([])
        }}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button
              onClick={() => {
                setIsEditModalVisible(false)
                setEditingPortfolio(null)
                setEditFileList([])
                setDeletedPhotos([])
              }}
              style={{ marginRight: 8 }}
            >
              取消
            </Button>
            <Button
              type='primary'
              onClick={handleUpdatePortfolio}
              loading={isUploading}
              disabled={isUploading}
            >
              更新
            </Button>
          </div>
        }
      >
        <Input
          size='large'
          placeholder='作品標題'
          value={newPortfolio.title}
          onChange={(e) =>
            setNewPortfolio({ ...newPortfolio, title: e.target.value })
          }
        />

        <div className='upload-area'>
          {(() => {
            const fileTypes = editFileList.map((f) => {
              if (f.isExisting) return 'EXISTING'
              const fileName = (
                f.name ||
                f.originFileObj?.name ||
                ''
              ).toLowerCase()
              if (/\.(heic|heif)$/i.test(fileName) || f.isHeic) return 'HEIC'
              if (/\.(jpeg|jpg)$/i.test(fileName)) return 'JPEG'
              if (/\.(png)$/i.test(fileName)) return 'PNG'
              if (/\.(gif)$/i.test(fileName)) return 'GIF'
              return 'OTHER'
            })
            const hasVideo = editFileList.some(
              (f) =>
                f.isVideo ||
                (f.originFileObj?.type || '').startsWith('video/')
            )
            return shouldShowCrop && !hasVideo ? (
              <ImgCrop
                rotate
                aspect={1}
                quality={1}
                modalTitle='裁剪圖片'
                modalOk='確定'
                modalCancel='取消'
                beforeCrop={beforeCropEdit}
              >
                <Upload
                  listType='picture-card'
                  fileList={editFileList}
                  onChange={handleEditImageUpload}
                  onRemove={handleEditImageRemove}
                  onPreview={onPreview}
                  beforeUpload={beforeUploadEdit}
                  multiple
                  disabled={isUploading}
                  customRequest={customRequest}
                  accept='image/*,video/mp4,video/quicktime,video/webm'
                >
                  {editFileList.length < 5 && (
                    <div>
                      <PlusOutlined
                        style={{ fontSize: '24px', marginBottom: '8px' }}
                      />
                      <div>上傳照片／影片</div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#888',
                          marginTop: '8px'
                        }}
                      >
                        圖片或 MP4／MOV／WEBM，最長 90 秒、單檔最大 40MB
                      </div>
                    </div>
                  )}
                </Upload>
              </ImgCrop>
            ) : (
              <Upload
                listType='picture-card'
                fileList={editFileList}
                onChange={handleEditImageUpload}
                onRemove={handleEditImageRemove}
                onPreview={onPreview}
                beforeUpload={beforeUploadEdit}
                multiple
                disabled={isUploading}
                customRequest={customRequest}
                accept='image/*,video/mp4,video/quicktime,video/webm'
              >
                {editFileList.length < 5 && (
                  <div>
                    <PlusOutlined
                      style={{ fontSize: '24px', marginBottom: '8px' }}
                    />
                    <div>上傳照片／影片</div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#888',
                        marginTop: '8px'
                      }}
                    >
                      圖片或 MP4／MOV／WEBM，最長 90 秒、單檔最大 40MB
                    </div>
                  </div>
                )}
              </Upload>
            )
          })()}
        </div>

        <Input.TextArea
          placeholder='作品內容'
          value={newPortfolio.content}
          onChange={(e) =>
            setNewPortfolio({ ...newPortfolio, content: e.target.value })
          }
          rows={6}
        />

        <Divider orientation='left'>選擇標籤</Divider>
        <TagContainer>
          {tags.map((tag) => (
            <EnhancedTag
              key={tag}
              color={newPortfolio.tags.includes(tag) ? '#1890ff' : ''}
              onClick={() => {
                const newTags = newPortfolio.tags.includes(tag)
                  ? newPortfolio.tags.filter((t) => t !== tag)
                  : [...newPortfolio.tags, tag]
                setNewPortfolio({ ...newPortfolio, tags: newTags })
              }}
            >
              {tag}
            </EnhancedTag>
          ))}
        </TagContainer>
      </AddPortfolioDrawer>
    </PageContainer>
  )
}

export default EmployeePortfolio
